import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { appendTeamMemberLawyerId } from "../../../../styles/styles";

export const createTag = createAsyncThunk(
  "createTag",
  async ({ url, body, token }) => {
    try {
      const response = await axios.post(url, body, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success()
      return response.data;
    } catch (error) {
      return toast.error(
        error.response?.data?.detail || "Failed to create tag"
      );
    }
  }
);

export const getAllTags = createAsyncThunk(
  "tag/getAllTags",
  async (
    { url, token, page, pageSize, search, teamMemberLawyerId },
    { rejectWithValue }
  ) => {
    let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
    const params = [];

    if (page !== undefined) {
      params.push(`page=${page}`);
    }
    if (pageSize !== undefined) {
      params.push(`page_size=${pageSize}`);
    }
    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }

    if (params.length > 0) {
      const separator = paginatedUrl.includes("?") ? "&" : "?";
      paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
    }

    try {
      const res = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      return {
        results: res.data.results || [],
        count: res.data.count || 0,
        next: res.data.next || null,
        previous: res.data.previous || null,
        currentPage: page || 1,
        pageSize: pageSize || 50,
      };
    } catch (error) {
      toast.error("Failed to get tags.");
      return rejectWithValue(error.response?.data || "Error fetching tags");
    }
  }
);

export const getAllRoles = createAsyncThunk(
  "getAllRoles",
  async ({ url }, { rejectWithValue }) => {
    try {
      const res = await axios.get(url);
      return res.data.data;
    } catch (error) {
      toast.error("Failed to get all roles.");
      return rejectWithValue(error.response?.data || "Error fetching roles");
    }
  }
);

export const getAllFilterContacts = createAsyncThunk(
  "contact/getAllFilterContacts",
  async (
    {
      url,
      token,
      page,
      pageSize,
      search,
      searchKey = "name",
      teamMemberLawyerId,
    },
    { rejectWithValue }
  ) => {
    let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
    const params = [];
    if (page !== undefined) {
      params.push(`page=${page}`);
    }
    if (pageSize !== undefined) {
      params.push(`page_size=${pageSize}`);
    }
    if (search) {
      params.push(`${searchKey}=${encodeURIComponent(search)}`);
    }
    if (params.length > 0) {
      const separator = paginatedUrl.includes("?") ? "&" : "?";
      paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
    }

    try {
      const res = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      return {
        ...res.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 50,
          rowCount: res.data.count || 0,
          nextPage: res.data.next,
          previousPage: res.data.previous,
        },
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching contacts");
    }
  }
);

export const getContactDetail = createAsyncThunk(
  "getContactDetail",
  async ({ url, token }, { rejectWithValue }) => {
    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return res.data;
    } catch (error) {
      toast.error("Failed to get contact detail.");
      return rejectWithValue(
        error.response?.data || "Error fetching contact detail"
      );
    }
  }
);

export const createContact = createAsyncThunk(
  "createContact",
  async ({ url, body, token }, { rejectWithValue }) => {
    try {
      const res = await axios.post(url, body, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      return res.data;
    } catch (error) {
      toast.error("Failed to create contact.");
      return rejectWithValue(error.response?.data || "Error creating contact");
    }
  }
);

export const updateTag = createAsyncThunk(
  "updateTag",
  async ({ url, data, token }) => {
    try {
      const response = await axios.put(url, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
);

// Slice
const contactSlice = createSlice({
  name: "contact",
  initialState: {
    allRoles: null,
    allTags: [],
    Tagpagination: {
      page: 1,
      pageSize: 50,
      count: 0,
      next: null,
      previous: null,
    },
    filteredContacts: null,
    filteredContactsPagenation: {
      page: 1,
      pageSize: 50,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    contactDetail: null,
    loading: false,
    tagLoading: false,
    error: null,
  },
  reducers: {
    // Add any additional reducers here if needed
    resetTagPagination: (state) => {
      state.Tagpagination = {
        page: 1,
        pageSize: 50,
        count: 0,
        next: null,
        previous: null,
      };
    },
    setCurrentTagPage: (state, action) => {
      state.Tagpagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContact.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error creating contact.";
      })
      .addCase(getAllRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.allRoles = action?.payload;
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload || "Error fetching roles.";
      })
      .addCase(createTag.pending, (state) => {
        state.tagLoading = true;
        state.error = null;
      })
      .addCase(createTag.fulfilled, (state) => {
        state.tagLoading = false;
      })
      .addCase(createTag.rejected, (state, action) => {
        state.tagLoading = false;
        state.error = action.payload || "Fail to create tag.";
      })
      .addCase(getAllTags.pending, (state) => {
        state.tagLoading = true;
        state.error = null;
      })
      .addCase(getAllTags.fulfilled, (state, action) => {
        state.tagLoading = false;
        state.allTags = action.payload.results;
        state.Tagpagination = {
          page: action.payload.currentPage,
          pageSize: action.payload.pageSize,
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(getAllTags.rejected, (state, action) => {
        state.tagLoading = false;
        state.error = action.payload || "Failed to get tags ";
      })
      .addCase(getAllFilterContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllFilterContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.filteredContacts = action?.payload?.results;
        state.filteredContactsPagenation = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAllFilterContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching filtered contacts.";
      })
      .addCase(getContactDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getContactDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.contactDetail = action.payload?.data;
      })
      .addCase(getContactDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Faild to get Contact Details";
      });
  },
});

export const { resetTagPagination, setCurrentTagPage } = contactSlice.actions;
export const contactReducer = contactSlice.reducer;
